import { getCaretOffset, locateOffset, readComposerText, setCaretOffset } from './composerCaret';

const BLOCK_LINE = /^(\s*)(?:([-*])[ \t]+|(\d+)\.[ \t]+|(>)[ \t]+)(.*)$/;

/**
 * Enter inside a list item or quote line: carries the block on to a new line at the caret, so
 * the text after it moves to the new item (numbers count up), or leaves the block when the item is empty.
 *
 * @param {HTMLElement} root The composer's contenteditable element.
 * @returns {boolean} True when the key was used here and must not send.
 */
export const continueBlock = root => {
    const line = currentLine(root);
    const match = line?.text.match(BLOCK_LINE);
    if (!match) return false;
    const [prefixed, indent, bullet, number, quote, rest] = match;
    // Before the item text, a plain line only; a new marker would double it.
    if (line.caret < line.start + prefixed.length - rest.length) return insertLineBreak();
    if (!rest.trim()) {
        selectOffsets(root, line.start, line.end);
        document.execCommand('delete');
        return true;
    }
    const marker = bullet || quote || `${Number(number) + 1}.`;
    insertLineBreak();
    document.execCommand('insertText', false, `${indent}${marker} `);
    return true;
};

/**
 * Down arrow on the last line of a list or quote: adds a plain line below it to step out.
 *
 * @param {HTMLElement} root The composer's contenteditable element.
 * @returns {boolean} True when the key was used here.
 */
export const exitBlockAtEnd = root => {
    const line = currentLine(root);
    if (!line || line.end !== line.length || !BLOCK_LINE.test(line.text)) return false;
    setCaretOffset(root, line.end);
    insertLineBreak();
    return true;
};

const currentLine = root => {
    const caret = getCaretOffset(root);
    if (caret === null) return null;
    const text = readComposerText(root);
    const start = text.lastIndexOf('\n', caret - 1) + 1;
    const lineEnd = text.indexOf('\n', caret);
    const end = lineEnd === -1 ? text.length : lineEnd;
    return { caret, start, end, length: text.length, text: text.slice(start, end) };
};

const selectOffsets = (root, start, end) => {
    const from = locateOffset(root, start);
    const to = locateOffset(root, end);
    const range = document.createRange();
    range.setStart(from.node, from.offset);
    range.setEnd(to.node, to.offset);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
};

// Firefox has no insertLineBreak command, so it gets the <br> directly.
const insertLineBreak = () => {
    if (!document.execCommand('insertLineBreak')) document.execCommand('insertHTML', false, '<br>');
    return true;
};
